import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Lightbox — full-screen image viewer with keyboard + click navigation.
 *
 * Props:
 *   images: (string | { url, caption })[] — array of image URLs or {url,caption}
 *   startIndex: number — which image to open first
 *   onClose: () => void
 */
export default function Lightbox({ images, startIndex = 0, onClose }) {
  const [index, setIndex] = useState(startIndex);

  const prev = useCallback(() => setIndex((i) => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIndex((i) => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [prev, next, onClose]);

  if (!images || images.length === 0) return null;
  const current = images[index];
  const currentUrl = typeof current === "string" ? current : current?.url;
  const currentCaption = typeof current === "string" ? "" : (current?.caption || "");

  return (
    <div
      className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center cursor-zoom-out"
      onClick={onClose}
      data-testid="lightbox"
    >
      {/* Close */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close"
        className="absolute top-5 right-5 p-3 text-white/90 hover:text-[var(--hinton-red)] transition-colors cursor-pointer z-10"
        data-testid="lightbox-close"
      >
        <X size={24} />
      </button>

      {/* Counter */}
      {images.length > 1 && (
        <div className="absolute top-6 left-6 text-xs uppercase tracking-[0.22em] font-mono text-white/60 z-10">
          {index + 1} / {images.length}
        </div>
      )}

      {/* Prev */}
      {images.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); prev(); }}
          aria-label="Previous"
          className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 p-3 text-white/70 hover:text-white hover:bg-white/5 transition-colors cursor-pointer z-10"
          data-testid="lightbox-prev"
        >
          <ChevronLeft size={32} />
        </button>
      )}

      {/* Image */}
      <img
        src={currentUrl}
        alt={currentCaption}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[92vw] max-h-[88vh] w-auto h-auto object-contain cursor-default"
        data-testid="lightbox-image"
      />

      {/* Caption */}
      {currentCaption && (
        <div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 max-w-[80vw] text-center text-xs sm:text-sm font-mono uppercase tracking-[0.18em] text-white/80 px-4"
          onClick={(e) => e.stopPropagation()}
          data-testid="lightbox-caption"
        >
          {currentCaption}
        </div>
      )}

      {/* Next */}
      {images.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); next(); }}
          aria-label="Next"
          className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 p-3 text-white/70 hover:text-white hover:bg-white/5 transition-colors cursor-pointer z-10"
          data-testid="lightbox-next"
        >
          <ChevronRight size={32} />
        </button>
      )}
    </div>
  );
}
